import {copyObject, json, MiniSignal} from "p3";
import common from "../../common";
import GameScene from "../../scenes/GameScene";
import settings from "../../settings";
import NPC from "../actor/npc/NPC";
import QuestFactory from "./QuestFactory";
import QuestMinigame from "./QuestMinigame";

export default class QuestManager {
    constructor() {
        this.signals = {
            init: new MiniSignal(),
            progress: new MiniSignal(),
            complete: new MiniSignal(),
            unassign: new MiniSignal()
        };

        this._activeQuests = {};
        this._completeQuests = {};
    }

    init() {
        for (const i in this._activeQuests) {
            if (this._activeQuests.hasOwnProperty(i)) {
                const quest = this._activeQuests[i];
                this.signals.init.dispatch(quest, false);
            }
        }
    }

    destroy() {
        P3.utils.destroyAllSignals(this.signals);
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    assignQuest(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let data;
        try {
            data = json(npc).quests[id];
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return false;
        }

        if (data) {
            if (!this.isQuestActive(id, npc) || this.isQuestBossFight(id, npc)) {
                const uid = npc + "|" + id;
                const quest = QuestFactory.create(data.type, id, npc);
                if (quest) {
                    quest.name = data.name;
                    quest.description = data.description;
                    quest.icon = data.icon;
                    quest.init(data.metadata ? data.metadata : {});

                    this._activeQuests[uid] = quest;
                    this.signals.init.dispatch(quest, true);

                    //console.log("[" + this.constructor.name + "]", "Quest '" + uid + "' assigned");

                    this.saveToActiveSlot();
                    return true;
                }
                console.warn("[" + this.constructor.name + "]", "Quest type '" + data.type + "' not valid");
            } else {
                console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' quest '" + id + "' already active");
            }
        } else {
            console.warn("[" + this.constructor.name + "]", "Quest '" + id + "' data not found");
        }
        return false;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @return {boolean}
     */
    unassignQuest(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        const quest = this._activeQuests[uid];
        if (quest) {
            delete this._activeQuests[uid];
            this.signals.unassign.dispatch(quest);

            //console.log("[" + this.constructor.name + "]", "Quest '" + uid + "' unassigned");

            this.saveToActiveSlot();
            return true;
        }
        return false;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @return {boolean}
     */
    completeQuest(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        if (this.isQuestActive(id, npc)) {
            const uid = npc + "|" + id;
            const quest = this._activeQuests[uid];
            this._completeQuests[uid] = quest;

            this.unassignQuest(id, npc);

            this.signals.complete.dispatch(quest);

            common.saveData.activeSlot.player.lastQuest = uid;
            common.saveData.saveGame();

            const completedQuests = QuestFactory.getCompletedQuests(common.saveData.activeSlot.quests);
            console.log('completed quests number: ', completedQuests);
            if(completedQuests == 6) {
                GameScene.active.signals.rate.dispatch();
            }

            //console.log("[" + this.constructor.name + "]", "Quest '" + uid + "' completed");
            return true;
        }
        return false;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     */
    forgetQuest(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        delete this._activeQuests[uid];
        delete this._completeQuests[uid];

        this.saveToActiveSlot();

        //console.log("[" + this.constructor.name + "]", "Quest '" + uid + "' forgotten");
    }

    /**
     * @param {function} predicate
     * @param {boolean} [init=false]
     */
    setProgressEach(predicate, init= false) {
        //console.log('QUEST', this._activeQuests);
        for (const i in this._activeQuests) {
            //console.log(i);
            if (this._activeQuests.hasOwnProperty(i)) {
                const quest = this._activeQuests[i];
                let value = predicate(quest);
                //console.log('value after predicate:', value);
                if (value == null) {
                    value = quest.value + 1;
                }
                if (value > quest.value) {
                    quest.value = value;
                    this.signals.progress.dispatch(quest, quest.progress, 0, init);

                    this.saveToActiveSlot();
                }
            }
        }
    }

    progressQuestManually(questId, addition) {

        if (!this._activeQuests.hasOwnProperty(questId)) {
            return false;
        }
        const quest = this._activeQuests[questId];
        quest.value += addition;
        if (quest.value < quest.target) {
            this.signals.progress.dispatch(quest, quest.progress, 0, false);
        }
        this.saveToActiveSlot();

        return quest.value >= quest.target;
    }

    /**
     * @param {NPC|string} npc
     * @return {string}
     */
    getCurrentQuestId(npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let metadata;
        try {
            metadata = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return undefined;
        }

        if (metadata) {
            let id;
            const quests = metadata.character.quests;
            for (let i = 0, count = quests.length; i < count; ++i) {
                id = quests[i];

                //console.log('Quest phase:', !(metadata.quests[id].phase > settings.PHASE), metadata.quests[id].phase, settings.PHASE);

                if (!(metadata.quests[id].phase > settings.PHASE) && this.isNPCQuestConditionsMet(id, npc)) {
                    if (this.isQuestMinigame(id, npc) || this.isQuestBossFight(id, npc)) {
                        return id;
                    } else {
                        if (!this.isQuestComplete(id, npc)) {
                            return id;
                        }
                    }
                }
            }
        }
        return undefined;
    }

    /**
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isNPCExistConditionsMet(npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let metadata;
        try {
            metadata = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return undefined;
        }

        let exists = true;
        if (metadata.character.temporary) {
            exists = !this.isQuestlineFinished(npc);
        }

        const conditions = metadata.character.conditions;
        if (conditions) {
            if (Array.isArray(conditions.active) && conditions.active.length > 0) {
                for (let i = 0, count = conditions.active.length; i < count; ++i) {
                    const uid = conditions.active[i];
                    const split = uid.split('|');
                    if (!this.isQuestActive(split[1], split[0])) {
                        exists = false;
                    }
                }
            }
            if (Array.isArray(conditions) && conditions.length > 0) {
                for (let i = 0, count = conditions.length; i < count; ++i) {
                    const uid = conditions[i];
                    const split = uid.split('|');
                    if (!this.isQuestComplete(split[1], split[0])) {
                        exists = false;
                    }
                }
            }
        }
        return exists;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isNPCQuestConditionsMet(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let metadata;
        try {
            metadata = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return undefined;
        }

        const conditions = metadata.quests[id].conditions;
        if (conditions) {
            if (Array.isArray(conditions.active) && conditions.active.length > 0) {
                for (let i = 0, count = conditions.active.length; i < count; ++i) {
                    const uid = conditions.active[i];
                    const split = uid.split('|');
                    if (!this.isQuestActive(split[1], split[0])) {
                        return false;
                    }
                }
            }
            if (Array.isArray(conditions.complete) && conditions.complete.length > 0) {
                for (let i = 0, count = conditions.complete.length; i < count; ++i) {
                    const uid = conditions.complete[i];
                    const split = uid.split('|');
                    if (!this.isQuestComplete(split[1], split[0])) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    /**
     * @param {NPC|string} npc
     * @return {boolean}
     */
    isQuestAvailable(npc) {
        return !!this.getCurrentQuestId(npc);
    }

    /**
     * @param {NPC|string} npc
     * @return {boolean}
     */
    isQuestlineFinished(npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let metadata;
        try {
            metadata = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return undefined;
        }

        if (metadata) {
            let id;
            const quests = metadata.character.quests;

            if (quests.length <= 0)
                return false;

            for (let i = 0, count = quests.length; i < count; ++i) {
                id = quests[i];

                if (!this.isQuestComplete(id, npc) || this.isQuestMinigame(id, npc) || this.isQuestBossFight(id, npc)) {
                    return false;
                }
            }
        }
        return true;
    }

    saveToActiveSlot() {
        const activeSlot = common.saveData.activeSlot;

        for (const i in this._activeQuests) {
            if (this._activeQuests.hasOwnProperty(i)) {
                const quest = this._activeQuests[i];
                if (!(quest instanceof QuestMinigame)) {
                    activeSlot.quests[i] = quest.value;
                }
            }
        }
        for (const i in this._completeQuests) {
            if (this._completeQuests.hasOwnProperty(i)) {
                const quest = this._completeQuests[i];
                if (quest instanceof QuestMinigame) {
                    activeSlot.quests[i] = quest.bestScore;
                } else {
                    activeSlot.quests[i] = true;
                }
            }
        }
        common.saveData.saveGame();
    }

    loadFromActiveSlot() {
        const activeSlot = common.saveData.activeSlot;

        let activeCount = 0, completeCount = 0;
        for (const i in activeSlot.quests) {
            if (activeSlot.quests.hasOwnProperty(i)) {
                const split = i.split("|");
                const id = split[1];
                const npc = split[0];

                const questValue = activeSlot.quests[i];
                const isActive = questValue !== true && !this.isQuestMinigame(id, npc) && !this.isQuestBossFight(id, npc);

                let data;
                try {
                    data = json(npc).quests[id];
                } catch {
                    console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
                }

                if (data) {
                    const uid = npc + "|" + id;
                    const quest = QuestFactory.create(data.type, id, npc);
                    if (quest) {
                        quest.name = data.name;
                        quest.description = data.description;
                        quest.icon = data.icon;
                        quest.init(data.metadata ? data.metadata : {});

                        if (isActive) {
                            quest.value = questValue;
                            this._activeQuests[uid] = quest;
                            ++activeCount;
                        } 
                        else if (questValue === true) { 
                            if (quest instanceof QuestMinigame) {
                                quest.bestScore = questValue;
                            }
                            this._completeQuests[uid] = quest;
                            ++completeCount;
                        }
                    }
                } else {
                    console.warn("[" + this.constructor.name + "]", "Quest '" + id + "' data not found");
                }
            }
        }
        //console.log("[" + this.constructor.name + "]", "Loaded quests - (active: " + activeCount + " complete: " + completeCount + ")");
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @return {object}
     */
    getQuestData(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let data;
        try {
            data = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return null;
        }

        const temp = data.quests[id];
        return copyObject(temp);
    }

    /**
     * @param {NPC|string} npc
     * @returns {number}
     */
    getQuestActiveCount(npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let count = 0;
        for (const i in this._activeQuests) {
            if (this._activeQuests.hasOwnProperty(i)) {
                const split = i.split("|");
                if (npc === split[0]) {
                    ++count;
                }
            }
        }
        return count;
    }

    /**
     * @param {NPC|string} npc
     * @returns {number}
     */
    getQuestCompleteCount(npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let count = 0;
        for (const i in this._completeQuests) {
            if (this._completeQuests.hasOwnProperty(i)) {
                const split = i.split("|");
                if (npc === split[0]) {
                    ++count;
                }
            }
        }
        return count;
    }

    /**
     * @param {number} id
     * @param {NPC|string} npc
     * @returns {number}
     */
    getMinigameScore(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        let quest = this._activeQuests[uid];
        !quest && (quest = this._completeQuests[uid]);
        return (quest instanceof QuestMinigame) ? quest.score : -1;
    }

    /**
     * @param {number} id
     * @param {NPC|string} npc
     * @returns {number}
     */
    getMinigameBestScore(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        let quest = this._activeQuests[uid];
        !quest && (quest = this._completeQuests[uid]);
        return (quest instanceof QuestMinigame) ? quest.bestScore : -1;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isQuestActive(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        const quest = this._activeQuests[uid];
        return quest && quest.id === id;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isQuestRequirementsMet(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        const quest = this._activeQuests[uid];
        return quest && quest.isRequirementsMet;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isQuestComplete(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);
        const uid = npc + "|" + id;
        return !!this._completeQuests[uid];
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isQuestMinigame(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let data, quest;
        try {
            data = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return false;
        }

        if (data) {
            try {
                quest = data.quests[id];
                return QuestFactory.isMinigame(quest.type);
            } catch {
                console.warn("[" + this.constructor.name + "]", "Quest '" + id + "' data not found");
            }
        }
        return false;
    }

    /**
     * @param {string} id
     * @param {NPC|string} npc
     * @returns {boolean}
     */
    isQuestBossFight(id, npc) {
        (npc instanceof NPC) && (npc = npc.id);

        let data, quest;
        try {
            data = json(npc);
        } catch {
            console.warn("[" + this.constructor.name + "]", "NPC '" + npc + "' data not found");
            return false;
        }

        if (data) {
            try {
                quest = data.quests[id];
                return QuestFactory.isBossFight(quest.type);
            } catch {
                console.warn("[" + this.constructor.name + "]", "Quest '" + id + "' data not found");
            }
        }
        return false;
    }

    /**
     * @returns {Array}
     */
    getActiveQuests(){
        let arr = [];
        for(let i in this._activeQuests){
            arr.push(this._activeQuests[i]);
        }
        return arr;
    }

    /**
     * @returns {Array}
     */
    getCompleteQuests(){
        let arr = [];
        for(let i in this._completeQuests){
            arr.push(this._completeQuests[i]);
        }
        return arr;
    }
}